import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Download, Mail, Phone, Building2, Briefcase, AlertCircle, 
  MapPin, MessageCircle, Check, Share2, Globe, Tag
} from "lucide-react";
import api from "../lib/api";
import axios from "axios";
import { tagClass } from "../components/TagPicker";
import BrandHeader from "../components/BrandHeader";

export default function SharePage() {
  const { token } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(true);
      setLoading(false);
      return;
    }

    const cleanToken = token.trim();

    const fetchLeadData = async () => {
      // 1. Configured API client
      try {
        const res = await api.get(`/share/${cleanToken}`);
        if (res?.data && (res.data.name !== undefined || res.data.id)) {
          setLead(res.data);
          setLoading(false);
          return;
        }
      } catch { /* ignore */ }

      // 2. Direct API path fallback
      try {
        const res2 = await axios.get(`/api/share/${cleanToken}`);
        if (res2?.data && (res2.data.name !== undefined || res2.data.id)) {
          setLead(res2.data);
          setLoading(false);
          return;
        }
      } catch { /* ignore */ }

      // 3. Root share path fallback
      try {
        const res3 = await axios.get(`/share/${cleanToken}`);
        if (res3?.data && (res3.data.name !== undefined || res3.data.id)) {
          setLead(res3.data);
          setLoading(false);
          return;
        }
      } catch { /* ignore */ }

      setError(true);
      setLoading(false);
    };

    fetchLeadData();
  }, [token]);

  const handleDownloadVCard = (e) => {
    e.preventDefault();
    setDownloading(true);
    const cleanToken = token?.trim();
    const downloadUrl = `/api/share/${cleanToken}/vcard`;
    
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.target = "_blank";
    a.setAttribute("download", `${lead?.name || "contact"}.vcf`);
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => setDownloading(false), 1500);
  };

  const displayName = (lead?.name || "").trim() || "Lead Contact";
  const initials = displayName
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const imageSrc = typeof lead?.image_base64 === "string" && lead.image_base64.trim()
    ? (lead.image_base64.startsWith("data:")
        ? lead.image_base64
        : `data:image/jpeg;base64,${lead.image_base64}`)
    : null;

  const rawPhone = lead?.phone || lead?.phone2 || "";
  const digitsOnly = rawPhone.replace(/\D/g, "");
  const cleanDigits = digitsOnly.replace(/^0+/, "");
  const waPhone = cleanDigits.length === 10 ? `91${cleanDigits}` : cleanDigits;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm font-bold text-zinc-500">
        Loading digital card…
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6">
        <div className="text-center max-w-sm p-8 bg-white border border-zinc-200 rounded-3xl shadow-sm">
          <AlertCircle className="h-12 w-12 text-zinc-400 mx-auto mb-3" />
          <h2 className="font-display text-2xl font-black text-zinc-950">Card Not Found</h2>
          <p className="text-xs text-zinc-500 mt-1">This shared link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 font-sans">
      <div className="max-w-md mx-auto px-5 pt-8 pb-16 space-y-4">
        
        {/* BRAND HEADER */}
        <div className="pb-1">
          <BrandHeader size="sm" />
        </div>

        {/* CARD CONTAINER */}
        <div className="bg-white rounded-3xl border border-zinc-200/90 p-6 shadow-sm space-y-5">
          
          {/* PROFILE HEADER */}
          <div className="flex items-start gap-4">
            {imageSrc ? (
              <img 
                src={imageSrc} 
                alt="Business Card" 
                className="h-16 w-16 rounded-2xl object-cover border border-zinc-200 shrink-0 shadow-xs" 
              />
            ) : (
              <div className="h-16 w-16 rounded-2xl bg-zinc-950 text-white font-display font-black text-xl flex items-center justify-center shrink-0 shadow-xs">
                {initials}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Digital Contact Card</p>
              <h1 className="font-display text-2xl font-black tracking-tight text-zinc-950 leading-tight mt-0.5 truncate">
                {displayName}
              </h1>

              {(lead.job_title || lead.company) && (
                <div className="text-xs text-zinc-600 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-medium">
                  {lead.job_title && (
                    <span className="inline-flex items-center gap-1 text-zinc-700">
                      <Briefcase className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                      <span>{lead.job_title}</span>
                    </span>
                  )}
                  {lead.company && (
                    <span className="inline-flex items-center gap-1 text-zinc-900 font-bold">
                      <Building2 className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                      <span>{lead.company}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* TAGS */}
          {lead.tags && lead.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {lead.tags.map((t) => (
                <span 
                  key={t} 
                  className={`px-3 py-0.5 rounded-full text-[11px] font-bold border ${
                    tagClass ? tagClass(t) : "bg-zinc-100 text-zinc-700 border-zinc-200"
                  }`}
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* QUICK ACTION BUTTONS */}
          <div className="grid grid-cols-2 gap-2.5 pt-1">
            {lead.phone && (
              <a
                href={`tel:${lead.phone}`}
                className="py-2.5 px-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 transition"
              >
                <Phone className="w-3.5 h-3.5 text-zinc-600" />
                <span>Call Mobile</span>
              </a>
            )}

            {waPhone && (
              <a
                href={`https://wa.me/${waPhone}`}
                target="_blank"
                rel="noreferrer"
                className="py-2.5 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 transition"
              >
                <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                <span>WhatsApp</span>
              </a>
            )}
          </div>

          {/* DETAILED CONTACT ROWS */}
          <div className="space-y-2 pt-1 border-t border-zinc-100 text-xs">
            {lead.email && (
              <a 
                href={`mailto:${lead.email}`} 
                className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-50/80 border border-zinc-200/80 hover:bg-zinc-100/80 transition"
              >
                <span className="w-8 h-8 rounded-xl bg-white border border-zinc-200 text-zinc-700 flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="font-semibold text-zinc-900 truncate">{lead.email}</span>
              </a>
            )}

            {lead.email2 && (
              <a 
                href={`mailto:${lead.email2}`} 
                className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-50/80 border border-zinc-200/80 hover:bg-zinc-100/80 transition"
              >
                <span className="w-8 h-8 rounded-xl bg-white border border-zinc-200 text-zinc-700 flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="font-semibold text-zinc-900 truncate">{lead.email2}</span>
              </a>
            )}

            {lead.phone && (
              <a 
                href={`tel:${lead.phone}`} 
                className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-50/80 border border-zinc-200/80 hover:bg-zinc-100/80 transition"
              >
                <span className="w-8 h-8 rounded-xl bg-white border border-zinc-200 text-zinc-700 flex items-center justify-center shrink-0">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="font-mono font-bold text-zinc-900">{lead.phone}</span>
              </a>
            )}

            {lead.phone2 && (
              <a 
                href={`tel:${lead.phone2}`} 
                className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-50/80 border border-zinc-200/80 hover:bg-zinc-100/80 transition"
              >
                <span className="w-8 h-8 rounded-xl bg-white border border-zinc-200 text-zinc-700 flex items-center justify-center shrink-0">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="font-mono font-bold text-zinc-900">{lead.phone2}</span>
              </a>
            )}

            {lead.address && (
              <div className="flex items-start gap-3 p-3 rounded-2xl bg-zinc-50/80 border border-zinc-200/80">
                <span className="w-8 h-8 rounded-xl bg-white border border-zinc-200 text-zinc-700 flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin className="h-4 w-4" />
                </span>
                <span className="text-zinc-700 leading-snug font-medium">{lead.address}</span>
              </div>
            )}
          </div>

          {/* SAVE VCARD BUTTON */}
          <button
            type="button"
            onClick={handleDownloadVCard}
            disabled={downloading}
            className="w-full py-3.5 bg-zinc-950 hover:bg-zinc-800 text-white font-extrabold text-xs uppercase tracking-wider rounded-2xl shadow-md transition flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
          >
            {downloading ? <Check className="h-4 w-4 text-emerald-400" /> : <Download className="h-4 w-4" />}
            <span>{downloading ? "Contact Saved (.vcf)" : "Save to Contacts (.vcf)"}</span>
          </button>

        </div>

        {/* FOOTER */}
        <p className="text-xs text-zinc-400 text-center pt-2">
          Powered by{" "}
          <Link to="/" className="text-zinc-700 font-bold underline underline-offset-4">
            ExpoLead
          </Link>{" "}
          — Smart Exhibition CRM
        </p>

      </div>
    </div>
  );
}